import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DataTable,
  Input,
  SpinnerLoader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  MultiSelectCheckboxTag,
  Switch,
  SingleSelect,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Edit, Trash2, Link, Code } from "lucide-react";
import { apiClientDrawerV2 as apiClient } from "@lift-admin/utils";
import { Toastify } from 'lift-ui';
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import jwtEncode from "jwt-encode";
import TableauViz from "./tableau-viz";

const REPORT_FIELD_SPECS: FieldSpec[] = [
  { key: "display_name", label: "Display Name" },
  { key: "embedded_url", label: "Embedded URL" },
  { key: "url_type", label: "URL Type" },
  { key: "description", label: "Description" },
  { key: "maintainer", label: "Maintainer" },
  { key: "contact_info", label: "Contact Info" },
  { key: "route_endpoint", label: "Route" },
];

/** Basic email format: local@domain.tld */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test((value || "").trim());
}
/** Validate contact info: empty is valid; if non-empty, must be one or more valid emails (comma or semicolon separated). */
function validateContactInfo(value: string | null | undefined): boolean {
  const trimmed = (value || "").trim();
  if (!trimmed) return true;
  const parts = trimmed.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
  return parts.length > 0 && parts.every(isValidEmail);
}

interface ItemsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: any;
  channels: any[];
  modules: any[];
  applications?: any[];
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

interface TabData {
  name: string;
  id: string;
  url: string;
  isActive: boolean;
}

const Reports = ({ toggle, searchText, openDialog, setOpenDialog, channels, modules, applications = [], hasEditAccess = true, hasManageAccess = true }: ItemsProps) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<any>([]);
  const [drawerItemBackUp, setDrawerItemsBackup] = useState<any>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openTabsDialog, setOpenTabsDialog] = useState<boolean>(false);
  const [openSeparateReportsDialog, setOpenSeparateReportsDialog] = useState<boolean>(false);
  const [detectedTabs, setDetectedTabs] = useState<TabData[]>([]);
  const [groupName, setGroupName] = useState<string>("");
  const [currentIdData, setCurrentIdData] = useState<any>(null);
  const [display_name, setDisplayName] = useState<string | null>(null);
  const [maintainer, setMaintainer] = useState<string | null>(null);
  const [contact_info, setContactInfo] = useState<string | null>(null);
  const [url_type, setURLType] = useState<string | null>(null);
  const [embedded_url, setEmbededURL] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [searchedResults, setSearchedResults] = useState<any>([]);
  const [checkTableauUrl, setCheckTableauUrl] = useState<string | null>(null);
  const [checkTableauToken, setCheckTableauToken] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [filterApplicationId, setFilterApplicationId] = useState<string>("");
  const [filterChannelId, setFilterChannelId] = useState<string>("");
  const [loadingAdd, setLoadingAdd] = useState<boolean>(false);
  const [loadingEdit, setLoadingEdit] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Column visibility for table
  const REPORT_SELECTABLE_COLUMN_KEYS = [
    "application", "channel", "grouping",
    "display_name", "embedded_url", "url_type", "description", "owner",
    "maintainer", "contact_info",
    "modified_by", "created_at", "updated_at",
  ];
  const [selectedReportColumns, setSelectedReportColumns] = useState<string[]>(() =>
    REPORT_SELECTABLE_COLUMN_KEYS.filter((k) => !["modified_by", "created_at", "updated_at"].includes(k))
  );

  // Tableau authentication credentials
  const connectedAppSecretId = "a55c4fbd-bdda-459d-8752-b1d5809224d6";
  const connectedAppSecretKey = "G1vY2QLbpnEE2FnwFI12rfBa2gYE3btdstoKYRP/jgY=";
  const connectedAppClientId = "2b62f4fd-2488-443d-abb4-d2287155f1bf";
  const siteName = "newengenlift";
  const currentUser = { email: "jteron@newengen.com" };

  // Function to create endpoint path
  const createEndPoint = (state: any) => {
    let endPointPath = "";

    if (state.selectedChannel?.name) {
      const channelPath = state.selectedChannel.name
        .toLowerCase()
        .replace(/\s+/g, "-");
      endPointPath += `/${channelPath}`;
    }

    if (state.selectedModule?.name) {
      const modulePath = state.selectedModule.name
        .toLowerCase()
        .replace(/\s+/g, "-");
      endPointPath += `/${modulePath}`;
    }

    endPointPath += `/`;

    return endPointPath;
  };

  // Function to get dynamic route_endpoint for a tab
  const getRouteEndpoint = (tab: TabData) => {
    const tabDisplayName = groupName ? `${groupName} - ${tab.name}` : tab.name;
    return createEndPoint({ selectedChannel, selectedModule }) + tabDisplayName.toLowerCase().replace(/\s+/g, "-");
  };

  const generateJWT = useCallback(() => {
    if (!currentUser?.email) {
      console.error("Invalid user email");
      return null;
    }
    try {
      const payload = {
        iss: connectedAppClientId,
        exp: Math.floor(Date.now() / 1000) + 5 * 60,
        jti: crypto.randomUUID(),
        aud: "tableau",
        sub: currentUser.email,
        scp: ["tableau:views:embed"],
      };
      return jwtEncode(payload, connectedAppSecretKey, {
        alg: "HS256",
        typ: "JWT",
        kid: connectedAppSecretId,
        iss: connectedAppClientId,
      });
    } catch (error) {
      console.error("JWT Generation Error:", error);
      return null;
    }
  }, [currentUser, connectedAppSecretId, connectedAppSecretKey, connectedAppClientId]);

  const authenticateWithTableau = useCallback(
    async (jwt: string) => {
      try {
        const response = await fetch(
          "https://us-east-1.online.tableau.com/vizportal/api/web/v1/auth/embed/signin",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Origin: window.location.origin,
            },
            credentials: "include",
            body: JSON.stringify({ siteName, jwt }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Authentication failed: ${JSON.stringify(errorData)}`);
        }
        const data = await response.json();
        console.log("Tableau authentication successful:", data);
        return data;
      } catch (error: any) {
        console.error("Authentication Error:", error);
        if (error.message.includes("401")) {
          throw new Error("Unauthorized | Please check Tableau credentials and user permissions");
        }
        throw error;
      }
    },
    [siteName]
  );

  const checkTableauReport = async (url: string): Promise<{ isTableau: boolean; tabs: TabData[] }> => {
    if (!url.toLowerCase().includes("tableau.com")) {
      console.log("Not a Tableau URL:", url);
      return { isTableau: false, tabs: [] };
    }
    try {
      const urlPattern = /^https:\/\/[a-z0-9-]+\.online\.tableau\.com\/t\/[a-zA-Z0-9_-]+\/views\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*$/;
      if (!urlPattern.test(url)) {
        console.log("Invalid Tableau URL format:", url);
        throw new Error("Invalid Tableau URL format.");
      }
      const jwt = generateJWT();
      if (!jwt) {
        throw new Error("Failed to generate JWT token.");
      }
      await authenticateWithTableau(jwt);
      setCheckTableauUrl(url);
      setCheckTableauToken(jwt);

      const tabs = await new Promise<TabData[]>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error("Timeout waiting for Tableau tabs");
          reject(new Error("Timeout waiting for Tableau viz to load"));
        }, 30000);
        const handleTabsLoaded = (tabs: TabData[]) => {
          console.log("Tabs loaded successfully:", tabs);
          clearTimeout(timeout);
          resolve(tabs);
        };
        const handleError = (error: string) => {
          clearTimeout(timeout);
          console.error("Error from TableauViz:", error);
          reject(new Error(error));
        };
        window.__tableauVizHandlers = { handleTabsLoaded, handleError };
      });
      return { isTableau: true, tabs };
    } catch (error: any) {
      console.error("Error in checkTableauReport:", error);
      Toastify("error", "Failed to check Tableau report tabs");
      return { isTableau: true, tabs: [] };
    } finally {
      setCheckTableauUrl(null);
      setCheckTableauToken(null);
    }
  };

  const getReports = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') as any);
      const res = await apiClient.get("/admin/reports", {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
          is_sandbox: false,
        }
      });
      const drawerReport = (res?.data || []).sort(
        (a: { created_at: string }, b: { created_at: string }) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setDrawerItems(searchText.trim().length === 0 ? drawerReport : drawerReport.filter((item: any) => searchedResults.includes(item.id)));
      setDrawerItemsBackup(drawerReport);
    } catch {
      Toastify("error", "Something went wrong");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => { getReports(); }, []);

  useEffect(() => {
    setPage(0);
  }, [filterApplicationId, filterChannelId]);

  const resetState = () => {
    setDisplayName(null);
    setURLType(null);
    setEmbededURL(null);
    setDescription(null);
    setCurrentIdData(null);
    setMaintainer(null);
    setContactInfo(null);
    setDetectedTabs([]);
    setGroupName("");
    setCheckTableauUrl(null);
    setCheckTableauToken(null);
    setSelectedChannel(null);
    setSelectedModule(null);
  };

  const handleAddChannel = async (reports: Array<{
    display_name: string;
    embedded_url: string;
    url_type: string;
    description: string;
    maintainer: string;
    contact_info: string;
    route_endpoint?: string;
  }>) => {
    setLoadingAdd(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (!user?.id) {
        Toastify("error", "User not found. Please login again.");
        return;
      }
      for (const report of reports) {
        if (!report.display_name || !report.url_type || !report.embedded_url || !report.description) {
          Toastify("error", "Please fill all required fields");
          return;
        }
        const payload = {
          display_name: report.display_name,
          embedded_url: report.embedded_url,
          url_type: report.url_type,
          description: report.description,
          owner: {
            id: user.id,
            name: user.name || "",
            email: user.email || ""
          },
          modified_by: user.id,
          maintainer: report.maintainer,
          contact_info: report.contact_info,
          route_endpoint: report.route_endpoint,
        };
        const res = await apiClient.post("/admin/reports", payload, {
          params: {
            user_id: user?.id,
            brand_id: user?.brand_id,
            is_admin: true,
            is_team: false
          }
        });
        const normalizedReport = {
          display_name: payload.display_name,
          embedded_url: payload.embedded_url,
          url_type: payload.url_type,
          description: payload.description,
          maintainer: payload.maintainer,
          contact_info: payload.contact_info,
          route_endpoint: payload.route_endpoint,
        };
        const desc = {
          message: buildSummary("Report", payload?.display_name, "created"),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "drawer",
          action: "create_report",
          reportName: payload?.display_name,
          user_id: user?.id,
          user_name: user?.name,
          details: buildDetails(normalizedReport, REPORT_FIELD_SPECS, {
            skipEmpty: true,
          }),
        };
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      }
      getReports();
      resetState();
      Toastify("success", "Report(s) added successfully");
      setOpenDialog((prev: any) => ({ ...prev, report: false }));
    } catch (err: any) {
      console.log(err, "err");
      Toastify("error", err.response?.data?.detail || "Failed to add report");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to create Report by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/admin/reports",
        category: "drawer",
        action: "create_report",
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleAddTableauTabs = async () => {
    setLoadingAdd(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const ownerId = user.id;
      const endpoint = createEndPoint({ selectedChannel, selectedModule }) + groupName.toLowerCase().replace(/\s+/g, "-");
      const grp = {
        name: groupName,
        path: endpoint,
        icon: "default-icon",
      };
      const tabs = detectedTabs.map((tab) => {
        const tabDisplayName = tab.name;
        const tabUrl = tab.url || `${embedded_url!.replace(/\/[^/]+$/, '')}/${tab.name}`;
        return {
          name: tabDisplayName,
          description: description!,
          url: tabUrl,
          url_type: url_type!,
          owner_id: ownerId,
          modified_by_id: ownerId,
          channel_id: selectedChannel?.id,
          contact_info: contact_info || "",
          maintainer: maintainer || "",
          module_id: selectedModule?.id,
          route_endpoint: createEndPoint({ selectedChannel, selectedModule }) + groupName.toLowerCase().replace(/\s+/g, "-") + "/" + tab.name.toLowerCase().replace(/\s+/g, "-"),
        };
      });
      const payload = { grp, tabs };
      const res = await apiClient.post("/drawer/v1/admin/drawer/structure/drawer-report-tableau-tabs", payload);
      Toastify("success", "Reports added successfully");
      const desc = {
        message: buildSummary("Report group", groupName, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_report_tableau_tabs",
        groupName,
        user_id: user?.id,
        user_name: user?.name,
        details: [
          { label: "Group Name", value: groupName || "Not Set" },
          {
            label: "Channel",
            value: selectedChannel?.name || "Not Set",
          },
          {
            label: "Module",
            value: selectedModule?.name || "Not Set",
          },
          {
            label: "Tabs",
            value:
              detectedTabs.map((tab) => tab.name).filter(Boolean).join(", ") ||
              "Not Set",
          },
          { label: "URL Type", value: url_type || "Not Set" },
          ...(description
            ? [{ label: "Description", value: description }]
            : []),
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      getReports();
      resetState();
    } catch (err: any) {
      Toastify("error", err.response?.data?.message || "Failed to add reports");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to create Report group "${groupName}" by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/drawer/v1/admin/drawer/structure/drawer-report-tableau-tabs",
        category: "drawer",
        action: "create_report_tableau_tabs",
        groupName,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleSubmit = async () => {
    if (!display_name || !embedded_url || !url_type || !description) {
      Toastify("error", "Please fill all required fields");
      return;
    }
    if (contact_info?.trim() && !validateContactInfo(contact_info)) {
      Toastify("error", "Please enter a valid email address for Contact Info (e.g. name@example.com). Multiple emails can be separated by comma or semicolon.");
      return;
    }
    setLoadingAdd(true);
    try {
      const { isTableau, tabs } = await checkTableauReport(embedded_url);
      if (isTableau && tabs.length > 1) {
        setDetectedTabs(tabs);
        setOpenTabsDialog(true);
      } else {
        const routeEndpoint = createEndPoint({ selectedChannel, selectedModule }) + display_name!.toLowerCase().replace(/\s+/g, "-");
        await handleAddChannel([{
          display_name: display_name!,
          embedded_url: embedded_url!,
          url_type: url_type!,
          description: description!,
          maintainer: maintainer || "",
          contact_info: contact_info || "",
          route_endpoint: routeEndpoint,
        }]);

      }
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleTabsDialogChoice = async (separate: boolean) => {
    if (!display_name || !embedded_url || !url_type || !description) return;
    setLoadingAdd(true);
    try {
      if (separate) {
        await handleAddTableauTabs();
      } else {
        const routeEndpoint = createEndPoint({ selectedChannel, selectedModule }) + display_name!.toLowerCase().replace(/\s+/g, "-");
        await handleAddChannel([{
          display_name: display_name!,
          embedded_url: embedded_url!,
          url_type: url_type!,
          description: description!,
          maintainer: maintainer || "",
          contact_info: contact_info || "",
          route_endpoint: routeEndpoint,
        }]);
      }
      setOpenSeparateReportsDialog(false);
      setOpenTabsDialog(false);
      setOpenDialog((prev: any) => ({ ...prev, report: false }));
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleDelete = async (data: any) => {
    setLoadingDelete(true);
    try {
      if (!data?.id) {
        Toastify("error", "Invalid Report ID");
        return;
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.id) {
        Toastify("error", "User not found. Please login again.");
        return;
      }
      const res = await apiClient.delete(`/admin/reports/${data?.id}`, {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false
        }
      });
      getReports();
      setOpenDeleteDialog(false);
      Toastify("success", "Item Deleted successfully");
      const normalizedReport = {
        display_name: data?.display_name,
        embedded_url: data?.embedded_url,
        url_type: data?.url_type,
        description: data?.description,
        maintainer: data?.maintainer,
        contact_info: data?.contact_info,
        route_endpoint: data?.route_endpoint,
      };
      const desc = {
        message: buildSummary("Report", data?.display_name, "deleted"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_report",
        reportId: data?.id,
        reportName: data?.display_name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails(normalizedReport, REPORT_FIELD_SPECS, {
          skipEmpty: true,
        }),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify("error", "Report not deleted");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to delete Report (Report ID: ${data?.id}, Report Name: "${data?.display_name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "DELETE",
        url: `/admin/reports/${data?.id}`,
        category: "drawer",
        action: "delete_report",
        reportId: data?.id,
        reportName: data?.display_name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleEdit = async () => {
    if (!currentIdData?.id || !display_name || !url_type || !embedded_url || !description) {
      Toastify("error", "Please fill all required fields");
      return;
    }
    if (contact_info?.trim() && !validateContactInfo(contact_info)) {
      Toastify("error", "Please enter a valid email address for Contact Info (e.g. name@example.com). Multiple emails can be separated by comma or semicolon.");
      return;
    }
    setLoadingEdit(true);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id) {
      Toastify("error", "User not found. Please login again.");
      return;
    }
    try {
      const payload = {
        id: currentIdData?.id,
        display_name,
        embedded_url,
        url_type,
        description,
        maintainer: maintainer || "",
        contact_info: contact_info || "",
        owner: currentIdData?.owner || {
          id: user.id,
          name: user.name || "",
          email: user.email || ""
        },
        created_at: currentIdData?.created_at,
        updated_at: new Date().toISOString(),
      };
      const res = await apiClient.post("/admin/reports", payload, {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false
        }
      });
      Toastify("success", "Item updated successfully");
      const previousReport = {
        display_name: currentIdData?.display_name,
        embedded_url: currentIdData?.embedded_url,
        url_type: currentIdData?.url_type,
        description: currentIdData?.description,
        maintainer: currentIdData?.maintainer || "",
        contact_info: currentIdData?.contact_info || "",
      };
      const nextReport = {
        display_name: payload.display_name,
        embedded_url: payload.embedded_url,
        url_type: payload.url_type,
        description: payload.description,
        maintainer: payload.maintainer,
        contact_info: payload.contact_info,
      };
      getReports();
      handleEditDialogClose();
      const updateFieldSpecs = REPORT_FIELD_SPECS.filter(
        (spec) => spec.key !== "route_endpoint"
      );
      const changes = diffFields(previousReport, nextReport, updateFieldSpecs);
      const desc = {
        message: buildSummary(
          "Report",
          payload.display_name || currentIdData?.display_name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_report",
        reportId: currentIdData?.id,
        reportName: payload.display_name || currentIdData?.display_name,
        user_id: user?.id,
        user_name: user?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify("error", err.response?.data?.detail || "Failed to update report");
      const errorDesc = {
        message: `Failed to update Report (Report ID: ${currentIdData?.id}, Report Name: "${currentIdData?.display_name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/admin/reports",
        category: "drawer",
        action: "update_report",
        reportId: currentIdData?.id,
        reportName: currentIdData?.display_name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog((prev: any) => ({ ...prev, report: false }));
    resetState();
  };

  const handleEditOpen = (data: any) => {
    setCurrentIdData(data);
    setDisplayName(data?.display_name);
    setEmbededURL(data?.embedded_url);
    setDescription(data?.description);
    setURLType(data?.url_type);
    setMaintainer(data?.maintainer);
    setContactInfo(data?.contact_info);
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setDisplayName(null);
    setEmbededURL(null);
    setDescription(null);
    setURLType(null);
    setCurrentIdData(null);
    setOpenEditDialog(false);
    setMaintainer(null);
    setContactInfo(null);
  };

  const allUrlTypes = ["Normal", "Fiscal", "FinTech"];

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerItemBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (
        filteredUsers[i]["display_name"]?.toLowerCase().includes(query.toLowerCase()) ||
        filteredUsers[i]["embedded_url"]?.toLowerCase().includes(query.toLowerCase()) ||
        filteredUsers[i]["url_type"]?.toLowerCase().includes(query.toLowerCase())
      )
        results.push(filteredUsers[i]);
    }
    setSearchedResults(results.map((item: any) => item.id));
    setDrawerItems(results);
  };

  useEffect(() => { searchResults(searchText); }, [searchText]);

  const authenticateTableauOnce = async () => {
    const jwt = generateJWT();
    if (!jwt) {
      throw new Error("Failed to generate JWT");
    }
    await authenticateWithTableau(jwt);
    return jwt;
  };

  const checkAndAddMissingTableauTabs = async () => {
    setLoadData(true);
    console.info(`[${new Date().toISOString()}] Starting checkAndAddMissingTableauTabs function`);

    try {
      // Fetch existing reports (parent reports only, not sandbox)
      console.info(`[${new Date().toISOString()}] Fetching existing reports from API`);
      const user = JSON.parse(localStorage.getItem('user') as any);
      const res = await apiClient.get("/admin/reports", {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
          is_sandbox: false,
        }
      });
      const existingReports = res?.data || [];
      console.info(`[${new Date().toISOString()}] Fetched ${existingReports.length} reports`);

      const tableauReports = existingReports.filter((report: any) =>
        report.embedded_url && report.embedded_url.toLowerCase().includes("tableau.com") && report.auto_tabs
      );
      console.info(`[${new Date().toISOString()}] Found ${tableauReports.length} Tableau reports to check`);

      // Authenticate once before processing reports
      console.info(`[${new Date().toISOString()}] Authenticating with Tableau...`);
      const jwt = await authenticateTableauOnce();
      console.info(`[${new Date().toISOString()}] Tableau authentication successful`);

      const missingTabs: any[] = [];

      // Process each Tableau report
      for (let i = 0; i < tableauReports.length; i++) {
        const report = tableauReports[i];
        console.info(`[${new Date().toISOString()}] [${i + 1}/${tableauReports.length}] Checking report: ${report.display_name} - URL: ${report.embedded_url}`);

        try {
          setCheckTableauUrl(report.embedded_url);
          setCheckTableauToken(jwt); // Reuse the single JWT

          console.info(`[${new Date().toISOString()}] Waiting for tabs to load for report: ${report.display_name}`);
          const tabs = await new Promise<TabData[]>((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.error(`[${new Date().toISOString()}] Timeout waiting for Tableau tabs for URL: ${report.embedded_url}`);
              reject(new Error(`Timeout waiting for Tableau viz to load for ${report.display_name}`));
            }, 30000); // 30-second timeout

            window.__tableauVizHandlers = {
              handleTabsLoaded: (tabs) => {
                clearTimeout(timeout);
                console.info(`[${new Date().toISOString()}] Loaded ${tabs.length} tabs for report ${report.display_name}`);
                resolve(tabs);
              },
              handleError: (error) => {
                clearTimeout(timeout);
                console.error(`[${new Date().toISOString()}] Error from TableauViz for ${report.display_name}:`, error);
                reject(new Error(error));
              }
            };
          });

          if (tabs.length > 0) {
            console.info(`[${new Date().toISOString()}] Found ${tabs.length} tabs for report ${report.display_name}. Checking for missing tabs...`);

            for (const tab of tabs) {
              const tabUrl = tab.url || `${report.embedded_url.replace(/\/[^/]+$/, '')}/${tab.name}`;
              const tabEndpoint = report.route_endpoint + "/" + tab.name.toLowerCase().replace(/\s+/g, "-");

              const tabExists = existingReports.some((existingReport: any) =>
                existingReport.embedded_url === tabUrl ||
                existingReport.route_endpoint === tabEndpoint
              );

              if (!tabExists) {
                if (missingTabs.length > 0) {
                  const existingTab = missingTabs.find((mt: any) => mt.tab.url === tabUrl);
                  if (existingTab) {
                    console.info(`[${new Date().toISOString()}] Tab ${tab.name} already in missing tabs list, skipping`);
                    continue;
                  }
                }

                console.info(`[${new Date().toISOString()}] Found missing tab: ${tab.name} for report ${report.display_name}`);
                missingTabs.push({
                  parentReport: report,
                  tab: { name: tab.name, url: tabUrl }
                });
              } else {
                console.info(`[${new Date().toISOString()}] Tab ${tab.name} already exists, skipping`);
              }
            }
          } else {
            console.info(`[${new Date().toISOString()}] No tabs found for report ${report.display_name}`);
          }
        } catch (error) {
          console.error(`[${new Date().toISOString()}] Failed for report ${report.display_name} (URL: ${report.embedded_url}):`, error);
          continue;
        } finally {
          setCheckTableauUrl(null);
          setCheckTableauToken(null);
        }
      }

      console.info(`[${new Date().toISOString()}] Found ${missingTabs.length} missing tabs that need to be added`);

      if (missingTabs.length > 0) {
        // Process each missing tab one at a time
        console.info(`[${new Date().toISOString()}] Starting to add ${missingTabs.length} missing tabs`);

        const addTabPromises = missingTabs.map((mt: any, index: number) => {
          return new Promise((resolve, reject) => {
            console.info(`[${new Date().toISOString()}] [${index + 1}/${missingTabs.length}] Adding tab ${mt.tab.name} for report ${mt.parentReport.display_name}`);

            apiClient.post("/drawer/v1/admin/drawer/structure/drawer-items-reports-tabs-auto", {
              parentId: mt.parentReport.id,
              tab: {
                name: mt.tab.name,
                url: mt.tab.url
              }
            })
              .then(res => {
                console.info(`[${new Date().toISOString()}] Successfully added tab ${mt.tab.name} for report ${mt.parentReport.display_name}`);
                resolve(res);
              })
              .catch(error => {
                console.error(`[${new Date().toISOString()}] Failed to add tab ${mt.tab.name} for report ${mt.parentReport.display_name}:`, error);
                reject(error);
              });
          });
        });

        // Wait for all tabs to be processed
        console.info(`[${new Date().toISOString()}] Waiting for all tab additions to complete...`);
        const results = await Promise.allSettled(addTabPromises);

        const successCount = results.filter(r => r.status === 'fulfilled').length;
        const failureCount = results.filter(r => r.status === 'rejected').length;

        console.info(`[${new Date().toISOString()}] All tab additions completed. Success: ${successCount}, Failed: ${failureCount}`);

        if (successCount > 0) {
          Toastify("success", `Successfully added ${successCount} missing tabs`);
        }

        if (failureCount > 0) {
          console.error(`[${new Date().toISOString()}] ${failureCount} tabs failed to add`);
        }
      } else {
        console.info(`[${new Date().toISOString()}] No missing tabs to add`);
        Toastify("info", "No missing tabs found to add");
      }

      return missingTabs.length > 0 ? true : true; // Return true even if no tabs were found, as the process completed successfully
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error checking Tableau reports:`, error);
      Toastify("error", `Failed to check Tableau reports: ${error || 'Unknown error'}`);
      return false;
    } finally {
      console.info(`[${new Date().toISOString()}] Completed checkAndAddMissingTableauTabs function`);
      setLoadData(false);
    }
  };

  // Channels for filter dropdown (by selected application)
  const filterChannelsByApplication = useMemo(() => {
    if (!filterApplicationId) return channels || [];
    return (channels || []).filter((ch: any) => String(ch.application_id) === String(filterApplicationId));
  }, [channels, filterApplicationId]);

  const getApplicationNameForChannel = (channel: any) => {
    if (!channel?.application_id || !applications?.length) return null;
    const app = applications.find((a: any) => String(a.id) === String(channel.application_id));
    return app ? (app.display_name ?? app.name) || String(app.id) : null;
  };

  const channelLabelWithParent = (ch: any) => {
    const name = ch?.name ?? String(ch?.id ?? "");
    const appName = getApplicationNameForChannel(ch);
    return appName ? `${name} (${appName})` : name;
  };

  // Filter reports by application and/or channel (used_in)
  const filteredReports = useMemo(() => {
    let list = drawerItem || [];
    if (filterApplicationId) {
      list = list.filter((row: any) =>
        (row.used_in || []).some((u: any) => String(u.application?.id) === String(filterApplicationId))
      );
    }
    if (filterChannelId) {
      list = list.filter((row: any) =>
        (row.used_in || []).some((u: any) => String(u.channel?.id) === String(filterChannelId))
      );
    }
    return list;
  }, [drawerItem, filterApplicationId, filterChannelId]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredReports.slice(start, end);
  }, [filteredReports, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredReports.length / rowsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const reportColumnLabels: Record<string, string> = {
    display_name: "Display Name",
    embedded_url: "Embedded URL",
    url_type: "URL Type",
    description: "Description",
    owner: "Owner",
    maintainer: "Maintainer",
    contact_info: "Contact Info",
    application: "Application",
    channel: "Channel",
    grouping: "Grouping",
    modified_by: "Modified By",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  type UsedInEntry = { application?: { name?: string }; channel?: { name?: string }; grouping?: { name: string } | null; item?: { display_name?: string } };
  const usedInApplications = (usedIn: UsedInEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.application?.name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };
  const usedInChannels = (usedIn: UsedInEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.channel?.name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };
  const usedInGroupings = (usedIn: UsedInEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.grouping?.name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex flex-wrap items-end justify-between gap-4 pb-3">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5 min-w-[160px]">
                <Label className="text-sm font-medium">Application</Label>
                <SingleSelect
                  options={[
                    { label: "All applications", value: "all" },
                    ...(applications || []).map((app: any) => ({
                      label: app.display_name ?? app.name ?? String(app.id),
                      value: String(app.id),
                    })),
                  ]}
                  defaultValue={filterApplicationId || "all"}
                  onChange={(value) => {
                    const val = value || "all";
                    setFilterApplicationId(val === "all" ? "" : val);
                    setFilterChannelId("");
                  }}
                  placeholder="All applications"
                  containerClassName="w-full"
                />
              </div>
              <div className="space-y-1.5 min-w-[160px]">
                <Label className="text-sm font-medium">Channel</Label>
                <SingleSelect
                  options={[
                    { label: "All channels", value: "all" },
                    ...filterChannelsByApplication.map((ch: any) => ({
                      label: channelLabelWithParent(ch),
                      value: String(ch.id),
                    })),
                  ]}
                  defaultValue={filterChannelId || "all"}
                  onChange={(value) => {
                    const val = value || "all";
                    setFilterChannelId(val === "all" ? "" : val);
                  }}
                  placeholder="All channels"
                  containerClassName="w-full"
                />
              </div>
            </div>
            <div className="space-y-1.5 min-w-[180px] max-w-[280px]">
              <Label htmlFor="columns-reports" className="text-sm font-medium">
                Columns
              </Label>
              <MultiSelectCheckboxTag
                placeholder={selectedReportColumns.length > 0 ? `Columns (${selectedReportColumns.length})` : "Select Columns"}
                options={REPORT_SELECTABLE_COLUMN_KEYS.map((key) => ({ value: key, label: reportColumnLabels[key] ?? key }))}
                value={selectedReportColumns}
                onChange={(ids: string[]) => ids.length > 0 && setSelectedReportColumns(ids)}
                containerClassName="w-full"
                showSelectAll
              />
            </div>
          </div>
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="100%"
          columns={(() => {
            const dataCols: TableColumn<any>[] = [
              { key: "application", header: "Application", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInApplications(row.used_in)}>{usedInApplications(row.used_in)}</span> },
              { key: "channel", header: "Channel", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInChannels(row.used_in)}>{usedInChannels(row.used_in)}</span> },
              { key: "grouping", header: "Grouping", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInGroupings(row.used_in)}>{usedInGroupings(row.used_in)}</span> },
              { key: "display_name", header: "Display Name", accessor: "display_name", width: "180px", render: (_v: any, row: any) => <span className="font-medium">{row.display_name}</span> },
              { key: "embedded_url", header: "Embedded URL", accessor: "embedded_url", width: "200px", render: (_v: any, row: any) => <span className="block max-w-[200px] truncate" title={row.embedded_url}>{row.embedded_url}</span> },
              { key: "url_type", header: "URL Type", accessor: "url_type", width: "100px" },
              { key: "description", header: "Description", accessor: "description", width: "200px", render: (_v: any, row: any) => <span className="block max-w-[200px] truncate" title={row.description}>{row.description}</span> },
              { key: "owner", header: "Owner", accessor: (row: any) => row.owner?.name || "", width: "120px" },
              { key: "maintainer", header: "Maintainer", accessor: "maintainer", width: "120px" },
              { key: "contact_info", header: "Contact Info", accessor: "contact_info", width: "120px" },
              { key: "modified_by", header: "Modified By", accessor: (row: any) => row.modified_by?.name || "", width: "120px" },
              { key: "created_at", header: "Created At", accessor: (row: any) => formatDate(row.created_at), width: "140px" },
              { key: "updated_at", header: "Updated At", accessor: (row: any) => formatDate(row.updated_at), width: "140px" },
            ];
            const filtered = REPORT_SELECTABLE_COLUMN_KEYS.filter((k) => selectedReportColumns.includes(k))
              .map((k) => dataCols.find((c) => c.key === k))
              .filter(Boolean) as TableColumn<any>[];
            if (showActionsColumn) {
              filtered.push({
                key: "actions",
                header: "Actions",
                accessor: (row: any) => row.id,
                width: "120px",
                align: "center",
                render: (_v: any, row: any) => (
                  <div className="flex gap-1 justify-center">
                    {hasEditAccess && (
                      <Button variant="ghost" size="sm" onClick={() => handleEditOpen(row)} className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {hasManageAccess && (
                      <Button variant="ghost" size="sm" onClick={() => { setOpenDeleteDialog(true); setCurrentIdData(row); }} className="h-8 w-8 p-0 text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ),
              });
            }
            return filtered;
          })()}
          data={paginatedData}
          loading={loadData}
          emptyMessage="No reports found."
          stickyHeader
          showActions={false}
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, totalPages),
            pageSize: rowsPerPage,
            totalItems: filteredReports.length,
            onPageChange: (p) => setPage(p - 1),
            onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
            pageSizeOptions: [10, 20, 50, 100],
          }}
          />
        </div>
      </div>

      {checkTableauUrl && checkTableauToken && (
        <TableauViz
          url={checkTableauUrl}
          token={checkTableauToken}
          onTabsLoaded={(tabs) => window.__tableauVizHandlers?.handleTabsLoaded(tabs as any)}
          onError={(error) => window.__tableauVizHandlers?.handleError(error)}
        />
      )}

      <button
        id="tableau-auto-script"
        onClick={async () => {
          try {
            console.info(`[${new Date().toISOString()}] Starting Tableau tabs synchronization...`);
            console.info(`[${new Date().toISOString()}] Calling checkAndAddMissingTableauTabs function`);
            const res = await checkAndAddMissingTableauTabs();
            console.info(`[${new Date().toISOString()}] Tableau synchronization completed with result: ${res}`);
            const btn = document.getElementById('tableau-auto-script');
            if (btn) {
              if (res === true) {
                btn.setAttribute('data-result', 'SUCCESS');
                btn.setAttribute('data-timestamp', new Date().toISOString());
                btn.setAttribute('data-message', 'Tableau tabs were successfully synchronized');
                console.info(`[${new Date().toISOString()}] DATA_RESULT: SUCCESS - Tableau tabs were successfully synchronized`);
              } else {
                btn.setAttribute('data-result', 'UNSUCCESSFUL');
                btn.setAttribute('data-timestamp', new Date().toISOString());
                btn.setAttribute('data-message', 'Failed to synchronize Tableau tabs');
                console.info(`[${new Date().toISOString()}] DATA_RESULT: UNSUCCESSFUL - Failed to synchronize Tableau tabs`);
              }
            }
          } catch (error) {
            console.error(`[${new Date().toISOString()}] ERROR during Tableau synchronization:`, error);
            const btn = document.getElementById('tableau-auto-script');
            if (btn) {
              btn.setAttribute('data-result', 'ERROR');
              btn.setAttribute('data-timestamp', new Date().toISOString());
              btn.setAttribute('data-message', `Error: ${error || 'Unknown error occurred'}`);
              console.info(`[${new Date().toISOString()}] DATA_RESULT: ERROR - ${error || 'Unknown error occurred'}`);
            }
          }
        }}
        style={{ color: '#f9f9f9' }}
      >Check Tableau Tabs</button>

      {/* Add New Report Sheet */}
      <Sheet open={openDialog} onOpenChange={(open) => {
        if (!open) handleDialogClose();
      }}>
        <SheetContent className="sm:max-w-[500px] flex flex-col h-full">
          <SheetHeader className="flex flex-row items-center justify-between gap-4">
            <SheetTitle>Add New Report</SheetTitle>
            {/* Team Access toggle aligned with close icon */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Team Access</span>
              <Switch
                checked={false}
                onCheckedChange={() => {}}
              />
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                value={display_name || ""}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="embedded_url">Embedded URL *</Label>
              <Input
                id="embedded_url"
                value={embedded_url || ""}
                onChange={(e) => setEmbededURL(e.target.value)}
                placeholder="Enter embedded URL"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="url_type">URL Type *</Label>
              <SingleSelect
                options={allUrlTypes.map((type) => ({ label: type, value: type }))}
                defaultValue={url_type || ""}
                onChange={(value) => setURLType(value)}
                placeholder="Select URL type"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={description || ""}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="maintainer">Maintainer</Label>
              <Input
                id="maintainer"
                value={maintainer || ""}
                onChange={(e) => setMaintainer(e.target.value)}
                placeholder="Enter maintainer"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_info">Contact Info (email)</Label>
              <Input
                id="contact_info"
                value={contact_info || ""}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="e.g. name@example.com (comma/semicolon for multiple)"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button variant="outline" onClick={handleDialogClose} disabled={loadingAdd}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loadingAdd}>
              {loadingAdd ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Report Sheet */}
      <Sheet open={openEditDialog} onOpenChange={(open) => {
        if (!open) handleEditDialogClose();
      }}>
        <SheetContent
          className="sm:max-w-[500px] flex flex-col h-full"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>Edit Report</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit_display_name">Display Name *</Label>
              <Input
                id="edit_display_name"
                value={display_name || ""}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_embedded_url">Embedded URL *</Label>
              <Input
                id="edit_embedded_url"
                value={embedded_url || ""}
                onChange={(e) => setEmbededURL(e.target.value)}
                placeholder="Enter embedded URL"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_url_type">URL Type *</Label>
              <SingleSelect
                options={allUrlTypes.map((type) => ({ label: type, value: type }))}
                defaultValue={url_type || ""}
                onChange={(value) => setURLType(value)}
                placeholder="Select URL type"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_description">Description *</Label>
              <Input
                id="edit_description"
                value={description || ""}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_maintainer">Maintainer</Label>
              <Input
                id="edit_maintainer"
                value={maintainer || ""}
                onChange={(e) => setMaintainer(e.target.value)}
                placeholder="Enter maintainer"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_contact_info">Contact Info (email)</Label>
              <Input
                id="edit_contact_info"
                value={contact_info || ""}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="e.g. name@example.com (comma/semicolon for multiple)"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button variant="outline" onClick={handleEditDialogClose}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={loadingEdit}>
              {loadingEdit ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure you want to delete?</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(currentIdData)} disabled={loadingDelete}>
              {loadingDelete ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tableau Tabs Detected Dialog */}
      <Dialog open={openTabsDialog} onOpenChange={setOpenTabsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tableau Report Tabs Detected</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This report contains multiple tabs: {detectedTabs.map((tab) => tab.name).join(", ")}. Do you want to save it as separate reports or as a single report?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setOpenTabsDialog(false); handleTabsDialogChoice(false); }}
              disabled={loadingAdd}
            >
              {loadingAdd ? "Saving..." : "Save as Single Report"}
            </Button>
            <Button
              onClick={() => { setOpenTabsDialog(false); setOpenSeparateReportsDialog(true); }}
              disabled={loadingAdd}
            >
              Save as Separate Reports
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Separate Reports Sheet */}
      <Sheet open={openSeparateReportsDialog} onOpenChange={setOpenSeparateReportsDialog}>
        <SheetContent className="sm:max-w-[600px] flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Save Separate Reports</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid gap-4 py-4">
            <h3 className="font-semibold">Drawer Location Details</h3>
            <div className="grid gap-2">
              <Label htmlFor="group_name">Group Name *</Label>
              <Input
                id="group_name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name"
              />
            </div>
            <div className="grid gap-2">
              <Label>Channel Name *</Label>
              <Select
                value={selectedChannel?.id?.toString() || ""}
                onValueChange={(value) => {
                  const channel = channels.find(c => c.id.toString() === value);
                  setSelectedChannel(channel);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Channel" />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((channel) => (
                    <SelectItem key={channel.id} value={channel.id.toString()}>
                      {channelLabelWithParent(channel)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Module Name *</Label>
              <Select
                value={selectedModule?.id?.toString() || ""}
                onValueChange={(value) => {
                  const mod = modules.find(m => m.id.toString() === value);
                  setSelectedModule(mod);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Module" />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((mod) => (
                    <SelectItem key={mod.id} value={mod.id.toString()}>
                      {mod.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <hr className="my-4" />

            <h3 className="font-semibold">Tabs</h3>
            <p className="text-sm text-gray-500">
              Please provide a name for each detected tab below.
            </p>

            {detectedTabs.map((tab, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Link className="h-4 w-4 text-gray-500" />
                  <span className="font-mono text-xs break-all">URL: {tab.url}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Code className="h-4 w-4 text-gray-500" />
                  <span className="font-mono text-xs break-all">Endpoint: {getRouteEndpoint(tab)}</span>
                </div>
                <div className="grid gap-2 mt-2">
                  <Label htmlFor={`tab_${index}`}>Tab {index + 1} Name</Label>
                  <Input
                    id={`tab_${index}`}
                    value={tab.name}
                    onChange={(e) => {
                      const updatedTabs = [...detectedTabs];
                      updatedTabs[index].name = e.target.value;
                      setDetectedTabs(updatedTabs);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button variant="outline" onClick={() => setOpenSeparateReportsDialog(false)} disabled={loadingAdd}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!groupName || !selectedChannel || !selectedModule) {
                  Toastify("error", "Please fill all required fields");
                  return;
                }
                handleTabsDialogChoice(true);
                setOpenSeparateReportsDialog(false);
              }}
              disabled={loadingAdd || !groupName || !selectedChannel || !selectedModule}
            >
              {loadingAdd ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};

declare global {
  interface Window {
    __tableauVizHandlers?: {
      handleTabsLoaded: (tabs: TabData[]) => void;
      handleError: (error: string) => void;
    };
  }
}

export default Reports;
